<script setup lang="ts">
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import IconCount from '@/components/Objects/IconCount.vue'
import { useShipStore } from '@/stores/shipStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarSelect from '@/components/Interface/DataTable/ActionBar/ActionBarSelect.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import { useTableStore } from '@/stores/tableStore.ts'
import MapShipMultiSelectMenu from '@/components/Interface/Menu/MapShipMultiSelectMenu.vue'
import type PlayerShip from '@/types/PlayerShip.ts'
import PercentageText from '@/components/Interface/Text/PercentageText.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import ShipAction from '@/components/Interface/ShipFleet/ShipAction.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const gameStore = useGameStore();
const tableStore = useTableStore();
const shipStore = useShipStore()
const shipActionStore = useShipActionStore()

// Load filtered ships list
const filters = (ships: PlayerShip[]) => {
  return ships
    .filter((ship) => {
      if(ship.fleetId === 0 && ship.xPos === gameStore.modalData.xPos && ship.yPos === gameStore.modalData.yPos) {
        return true;
      }
    })
    .filter((ship) => {
      // If no category is selected, return all ships
      if (!tableStore.mapShips.filters.selectedAction || tableStore.mapShips.filters.selectedAction === '') {
        return true;
      }
      // Otherwise, filter based on the selected category
      return ship.currentActionName === tableStore.mapShips.filters.selectedAction;
    })
    .filter((ship) => {
      // If nothing is selected, return all ships
      if (!tableStore.mapShips.filters.selectedType || tableStore.mapShips.filters.selectedType === '') {
        return true;
      }
      // Otherwise, filter based on the selected type
      return ship.type === tableStore.mapShips.filters.selectedType;
    })
    .filter((ship) => {
      // If no search query, return all ships
      if (!tableStore.mapShips.filters.nameSearch || tableStore.mapShips.filters.nameSearch === '') {
        return true;
      }
      // Otherwise, filter based on the ship name (case-insensitive search)
      return ship.shipName.toLowerCase().includes(tableStore.mapShips.filters.nameSearch.toLowerCase());
    });
};

// Double-click event
const doubleClick = (id:number) => {
  shipActionStore.initShipsFromIds([id]);
  tableStore.mapShips.selectedAll = false
  gameStore.modalType=''
};

</script>

<template>
  <div class="text-white ">
    <FerionDataTable
      :data="shipStore.ships"
      tableStoreName="mapShips"
      :selector="true"
      :filters="filters"
      :heightLimit="'h-[calc(100vh-23.75rem)]'"
      :double-click="doubleClick"
    >
      <!--- ActionBar --->
      <template #actionBar>
        <ActionBarBlock class="mr-1 flex items-center rounded">
          <MapShipMultiSelectMenu></MapShipMultiSelectMenu>
        </ActionBarBlock>
        <ActionBarFiller class="mr-1 rounded"></ActionBarFiller>
        <ActionBarSearch v-model="tableStore.mapShips.filters.nameSearch" :name="t('modals.mapSelect.ships.name')" :placeHolder="t('modals.mapSelect.ships.searchPlaceholder')" class="mr-1"/>
        <ActionBarSelect :name="t('modals.mapSelect.ships.type')" width="6" v-model="tableStore.mapShips.filters.selectedType" class="mr-1">
          <option value="">{{ t('modals.mapSelect.ships.typeFilter.all') }}</option>
          <option value="scout">{{ t('modals.mapSelect.ships.typeFilter.scout') }}</option>
          <option value="settler">{{ t('modals.mapSelect.ships.typeFilter.settler') }}</option>
          <option value="bomber">{{ t('modals.mapSelect.ships.typeFilter.bomber') }}</option>
          <option value="fighter">{{ t('modals.mapSelect.ships.typeFilter.fighter') }}</option>
        </ActionBarSelect>
        <ActionBarSelect :name="t('modals.mapSelect.ships.action')" width="6" v-model="tableStore.mapShips.filters.selectedAction">
          <option value="">{{ t('modals.mapSelect.ships.actionFilter.all') }}</option>
          <option value="Idle">{{ t('modals.mapSelect.ships.actionFilter.idle') }}</option>
          <option value="Move">{{ t('modals.mapSelect.ships.actionFilter.move') }}</option>
          <option value="Attack">{{ t('modals.mapSelect.ships.actionFilter.attack') }}</option>
          <option value="Settle">{{ t('modals.mapSelect.ships.actionFilter.settle') }}</option>
          <option value="Follow">{{ t('modals.mapSelect.ships.actionFilter.follow') }}</option>
          <option value="FollowAttack">{{ t('modals.mapSelect.ships.actionFilter.followAttack') }}</option>
          <option value="Repair">{{ t('modals.mapSelect.ships.actionFilter.repair') }}</option>
          <option value="Defend">{{ t('modals.mapSelect.ships.actionFilter.defend') }}</option>
        </ActionBarSelect>
      </template>
      <!--- Headers --->
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex min-width="12" column="shipName" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tl">
          {{ t('modals.mapSelect.ships.name') }}
        </HeaderTextFlex>
        <HeaderFixedWidthIcon width="5" column="healthPercentage" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.hitPoints')" type="hit_points"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="maxSpeed" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.maxSpeed')" type="max_speed"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="shields" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.shields')" type="shields"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="attackShips" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.attackShips')" type="attack_against_ships"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="attackPlanets" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.attackPlanets')" type="attack_against_planets"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="scanningRange" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.scanRange')" type="scanning_range"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon width="3" column="repair" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort">
          <GenericIcon :tooltip="t('modals.mapSelect.ships.tooltips.repair')" type="repair"/>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthText width="10" column="currentActionName" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" align="center">
          {{ t('modals.mapSelect.ships.action') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthIcon width="3" column="etaNextHop" :sortColumn="sortColumn" :sortDirection="sortDirection" @sort="sort" class="rounded-tr">
          {{ t('modals.mapSelect.ships.eta') }}
        </HeaderFixedWidthIcon>
      </template>
      <!--- Rows --->
      <template #rows="{ item }" >
        <CellFlexWidth min-width="12">
          <div class="flex-col">
            <div>{{ item.shipName }}</div>
          </div>
        </CellFlexWidth>
        <CellFixedWidth width="5">
          <PercentageText :value="item.healthPercentage"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.maxSpeed" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.shields" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.attackShips" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.attackPlanets" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.scanningRange" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          <IconCount :amount="item.repair" :no-plus="true"/>
        </CellFixedWidth>
        <CellFixedWidth width="10" align="center">
          <ShipAction :ship-id="item.id"/>
        </CellFixedWidth>
        <CellFixedWidth width="3">
          {{ item.etaNextHop === 0 ? '?' : item.etaNextHop >= 500 ? '500+' : item.etaNextHop }}
        </CellFixedWidth>
      </template>
    </FerionDataTable>
  </div>
</template>

<style scoped lang="scss">
</style>
